"use client";

import React, { useState, useMemo } from "react";
import { AllAMCListData } from "@/type/commonTypes";
import { ROOT_URL } from "@/utils/gloableVariables";
import Image from "next/image";
import Icon from "../icons/Icons";

export default function OPAllAmcSection({
  allAmc,
}: {
  allAmc: AllAMCListData[];
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAmcs = useMemo(() => {
    if (!searchTerm) return allAmc;
    const lowerSearch = searchTerm.toLowerCase();
    return allAmc.filter(
      (item) =>
        item.amc?.toLowerCase().includes(lowerSearch) ||
        item.display_name?.toLowerCase().includes(lowerSearch) ||
        item.amc_code?.toLowerCase().includes(lowerSearch),
    );
  }, [allAmc, searchTerm]);

  return (
    <>
      {/* Top Bar: Search & Count */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-6 sm:mb-8">
        <div className="relative flex items-center gap-2 w-full max-w-80 px-3 py-2.5 bg-white border border-[#EBF0F6] rounded-2lg transition hover:border-primaryBlue/20">
          <Icon.searchIcon className="text-black/40 shrink-0" />
          <input
            type="text"
            className="text-sm text-primaryBlack/90 placeholder:text-primaryBlack/60 focus:outline-hidden"
            placeholder="Search AMCs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="text-gray-500 text-sm">
          Showing{" "}
          <span className="font-semibold text-gray-900">
            {filteredAmcs.length} Partners
          </span>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {filteredAmcs.map((item) => (
          <a
            key={item.id}
            href={`${ROOT_URL}${item.url || ""}`}
            className="group flex flex-col justify-between border border-[#EBF0F6] rounded-xl sm:rounded-2xl p-3 xs:p-4 md:p-6 bg-white transition hover:border-primaryBlue hover:bg-[#ECF3FF]"
          >
            <div className="flex items-center justify-between gap-5 mb-4">
              <Image
                src={`${ROOT_URL}img/AMCsLogo/${item.amc_code}.png`}
                alt={item.amc || "AMC"}
                width={52}
                height={52}
                className="w-12 h-12 object-contain rounded-lg border border-black/10"
              />

              <div className="text-right xs:text-center">
                <p className="text-primaryBlack/70 text-xs">Schemes</p>
                <span className="text-primaryBlack font-medium text-sm sm:text-base">{item.scheme_count}</span>
              </div>
            </div>

            <div
              title={item.display_name || ""}
              className="font-semibold text-[#111625] text-sm sm:text-base mb-1 line-clamp-2"
            >
              {item.display_name}
            </div>

            <p
              title={item.amc || ""}
              className="text-[#9A9A9A] text-2xs line-clamp-2"
            >
              {item.amc || "AMC"}
            </p>

            <div className="mt-2.5 pt-2.5 sm:mt-3.5 sm:pt-3.5 border-t border-[#EBF0F6] flex justify-between items-center text-[#4E586E] transition group-hover:border-primaryBlue group-hover:text-primaryBlue">
              <span className="text-xs">Explore Funds</span>
              <Icon.rightArrow />
            </div>
          </a>
        ))}
      </div>
    </>
  );
}
